/**
 * Detail/curation panel for one event type in the Event Catalog (GDN-2681).
 *
 * Three zones, mirroring column ownership in the backend:
 * - Declaration mirrors (code-owned, read-only chips): tenancy,
 *   domain/audit flags, privacy, source module.
 * - Observed reality (sweep-owned, read-only): per-tier first/last-seen
 *   and counts.
 * - Curation (human-owned): display label, description,
 *   category, notes — plus tag membership. Tag edits reshape tag-driven
 *   activity surfaces retroactively.
 *
 * The payload schema (from the code registry) and a sampled real payload
 * render below for the "what does this event actually look like" question.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import type { EventCatalogEntry, EventTypeEvent } from '../store/api';
import {
  useAssignEventTagMutation,
  useCreateEventTagMutation,
  useGetEventCatalogEntryQuery,
  useLazyListEventTypeEventsQuery,
  useListEventTagsQuery,
  useUnassignEventTagMutation,
  useUpdateEventCatalogEntryMutation,
} from '../store/hooks';

const CATEGORIES = [
  'platform',
  'control_plane',
  'resources',
  'cortex',
  'application',
  'billing',
  'telemetry',
  'integration',
];

const formatTimestamp = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
};

const DeclarationChips = ({ entry }: { entry: EventCatalogEntry }) => {
  if (entry.observed_only) {
    return (
      <Tooltip title="Seen in the analytics store but never registered in code — no declaration exists.">
        <Chip size="xSmall" variant="soft" color="warning" label="unregistered" />
      </Tooltip>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {entry.tenancy && <Chip size="xSmall" variant="soft" label={`tenancy: ${entry.tenancy}`} />}
      <Tooltip
        title={
          entry.domain_event
            ? 'Domain event: a statement of business fact consumers may rely on — carries a delivery/schema contract, never casually renamed.'
            : 'Telemetry (a.k.a. interaction exhaust): clicks, views, usage signals emitted as a byproduct. Best-effort, no consumer contract — the opposite of a domain event.'
        }
      >
        <Chip
          size="xSmall"
          variant="soft"
          color={entry.domain_event ? 'success' : 'default'}
          label={entry.domain_event ? 'domain event' : 'telemetry'}
        />
      </Tooltip>
      {entry.audit && (
        <Tooltip title="Audit: a compliance record — who did/saw what. Retained and queryable as evidence.">
          <Chip size="xSmall" variant="soft" color="info" label="audit" />
        </Tooltip>
      )}
      {entry.privacy && (
        <Chip
          size="xSmall"
          variant="soft"
          color={entry.privacy === 'none' ? 'default' : 'error'}
          label={`privacy: ${entry.privacy}`}
        />
      )}
      {entry.source_module && (
        <Chip size="xSmall" variant="soft" label={entry.source_module} />
      )}
    </Box>
  );
};

const ObservedStats = ({ entry }: { entry: EventCatalogEntry }) => {
  const tiers = Object.entries(entry.observed_stats ?? {});
  if (!tiers.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        Never observed in the analytics store.
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      {tiers.map(([tier, stats]) => {
        const tierStats = stats as {
          first_seen_at?: string;
          last_seen_at?: string;
          event_count?: number;
        };
        return (
          <Box key={tier} sx={{ minWidth: 200 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
              {tier}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {(tierStats.event_count ?? 0).toLocaleString()} events
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              first {formatTimestamp(tierStats.first_seen_at)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              last {formatTimestamp(tierStats.last_seen_at)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const TagEditor = ({ entry }: { entry: EventCatalogEntry }) => {
  const { data: tagData } = useListEventTagsQuery();
  const [assignTag, { isLoading: assigning }] = useAssignEventTagMutation();
  const [unassignTag] = useUnassignEventTagMutation();
  const [createTag, { isLoading: creating }] = useCreateEventTagMutation();
  const [pickerValue, setPickerValue] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [newTagDescription, setNewTagDescription] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const assigned = entry.tags ?? [];
  const assignable = (tagData?.tags ?? []).filter(tag => !assigned.includes(tag.tag_name));

  const run = async (action: () => Promise<unknown>) => {
    setMutationError(null);
    try {
      await action();
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message;
      setMutationError(message ?? 'The catalog rejected that edit.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {assigned.map(tag => (
          <Chip
            key={tag}
            size="small"
            variant="soft"
            color="info"
            label={tag}
            onDelete={() =>
              void run(() =>
                unassignTag({ eventType: entry.event_type, tagName: tag }).unwrap(),
              )
            }
          />
        ))}
        {!assigned.length && (
          <Typography variant="caption" color="text.secondary">
            No tags.
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            label="Add tag"
            value={pickerValue}
            onChange={e => {
              const tagName = e.target.value;
              setPickerValue('');
              if (tagName) {
                void run(() =>
                  assignTag({ eventType: entry.event_type, tagName }).unwrap(),
                );
              }
            }}
            disabled={assigning || !assignable.length}
            sx={{ minWidth: 140 }}
          >
            {assignable.map(tag => (
              <Tooltip key={tag.tag_name} title={tag.description} placement="left">
                <MenuItem value={tag.tag_name}>{tag.tag_name}</MenuItem>
              </Tooltip>
            ))}
          </TextField>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setShowCreate(v => !v)}>
            New tag
          </Button>
        </Box>
      {showCreate && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Tag name"
            value={newTagName}
            onChange={e => setNewTagName(e.target.value)}
            placeholder="e.g. compliance"
          />
          <TextField
            size="small"
            label="Description (required)"
            value={newTagDescription}
            onChange={e => setNewTagDescription(e.target.value)}
            sx={{ minWidth: 260, flex: 1 }}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={creating || !newTagName.trim() || !newTagDescription.trim()}
            onClick={() =>
              void run(async () => {
                const tagName = newTagName.trim().toLowerCase();
                await createTag({
                  createEventTagRequest: {
                    tag_name: tagName,
                    description: newTagDescription,
                  },
                }).unwrap();
                await assignTag({
                  eventType: entry.event_type,
                  tagName,
                }).unwrap();
                setNewTagName('');
                setNewTagDescription('');
                setShowCreate(false);
              })
            }
          >
            Create &amp; assign
          </Button>
        </Box>
      )}
      {mutationError && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {mutationError}
        </Alert>
      )}
    </Box>
  );
};

const CurationForm = ({ entry }: { entry: EventCatalogEntry }) => {
  const [updateEntry, { isLoading: saving }] = useUpdateEventCatalogEntryMutation();
  const [form, setForm] = useState({
    display_label: entry.display_label ?? '',
    description: entry.description ?? '',
    category: entry.category ?? '',
    notes: entry.notes ?? '',
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  const dirty =
    form.display_label !== (entry.display_label ?? '') ||
    form.description !== (entry.description ?? '') ||
    form.category !== (entry.category ?? '') ||
    form.notes !== (entry.notes ?? '');

  const save = async () => {
    setSaveError(null);
    try {
      await updateEntry({
        eventType: entry.event_type,
        updateEventCatalogEntryRequest: {
          display_label: form.display_label || null,
          description: form.description || null,
          category: form.category || null,
          notes: form.notes || null,
        },
      }).unwrap();
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message;
      setSaveError(message ?? 'Saving failed.');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Display label"
          value={form.display_label}
          onChange={e => setForm(f => ({ ...f, display_label: e.target.value }))}
          sx={{ minWidth: 220, flex: 1 }}
          helperText="Presentation only — never in APIs or filters."
        />
        <TextField
          select
          size="small"
          label="Category"
          value={form.category}
          onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">—</MenuItem>
          {CATEGORIES.map(category => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <TextField
        size="small"
        label="Description"
        value={form.description}
        onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
        multiline
        minRows={2}
      />
      <TextField
        size="small"
        label="Notes"
        value={form.notes}
        onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
        multiline
        minRows={1}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          disabled={!dirty || saving}
          onClick={() => void save()}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
        >
          Save metadata
        </Button>
        {saveError && (
          <Typography variant="caption" color="error">
            {saveError}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const PAGE_SIZE = 25;

interface EventsCursor {
  occurredAt: string;
  eventId: string;
}

const OccurrenceRow = ({ event }: { event: EventTypeEvent }) => {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <TableRow hover onClick={() => setOpen(previous => !previous)} sx={{ cursor: 'pointer' }}>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={open ? 'Collapse' : 'Expand'}>
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTimestamp(event.occurred_at)}</TableCell>
        <TableCell>
          {event.organization_id ? (
            <Link
              component={RouterLink}
              to={`/admin-v2/organization/${event.organization_id}`}
              variant="body2"
              onClick={clickEvent => clickEvent.stopPropagation()}
              sx={{ fontFamily: 'monospace', fontSize: 12 }}
            >
              {event.organization_id.slice(0, 12)}…
            </Link>
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell>
          {event.actor_kind === 'system' ? (
            <Typography variant="body2" color="text.secondary">
              ⚙ system
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
              {event.actor_id ?? event.user_id ?? '—'}
            </Typography>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontFamily: 'monospace' }}
              >
                event_id: {event.event_id}
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  mt: 1,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  fontSize: 12,
                  overflowX: 'auto',
                  maxHeight: 280,
                }}
              >
                {JSON.stringify(event.data ?? {}, null, 2)}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

/** The type's occurrences, newest first, payloads inline — keyset-paged
 * accumulation like the Activity tabs. Filters (org/actor/date) land here. */
const EventOccurrences = ({ eventType }: { eventType: string }) => {
  const [fetchPage, { isFetching }] = useLazyListEventTypeEventsQuery();
  const [events, setEvents] = useState<EventTypeEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<EventsCursor | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const requestId = useRef(0);

  const loadPage = useCallback(
    async (cursor: EventsCursor | null) => {
      const id = ++requestId.current;
      try {
        const result = await fetchPage({
          eventType,
          limit: PAGE_SIZE,
          ...(cursor ? { beforeOccurredAt: cursor.occurredAt, beforeEventId: cursor.eventId } : {}),
        }).unwrap();
        if (id !== requestId.current) return;
        setEvents(previous => {
          const page = result.entries ?? [];
          if (!cursor) return page;
          const seen = new Set(previous.map(event => event.event_id));
          return [...previous, ...page.filter(event => !seen.has(event.event_id))];
        });
        setNextCursor(
          result.has_more && result.next_before_occurred_at && result.next_before_event_id
            ? {
                occurredAt: result.next_before_occurred_at,
                eventId: result.next_before_event_id,
              }
            : null,
        );
        setFailed(false);
      } catch {
        if (id !== requestId.current) return;
        setFailed(true);
      } finally {
        if (id === requestId.current) setIsLoading(false);
      }
    },
    [fetchPage, eventType],
  );

  useEffect(() => {
    setIsLoading(true);
    setEvents([]);
    setNextCursor(null);
    void loadPage(null);
  }, [loadPage]);

  if (isLoading) return <CircularProgress size={18} />;
  if (failed && !events.length) {
    return (
      <Alert severity="warning">The analytics store did not answer; events are unavailable.</Alert>
    );
  }
  if (!events.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        No occurrences in the analytics store.
      </Typography>
    );
  }
  return (
    <>
      <Table size="small" aria-label="Event occurrences">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>When</TableCell>
            <TableCell>Organization</TableCell>
            <TableCell>Actor</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map(event => (
            <OccurrenceRow key={event.event_id} event={event} />
          ))}
        </TableBody>
      </Table>
      {nextCursor && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 1.5 }}>
          {failed && (
            <Typography variant="caption" color="warning.main">
              Loading more failed —
            </Typography>
          )}
          <Button
            size="small"
            onClick={() => void loadPage(nextCursor)}
            disabled={isFetching}
            startIcon={isFetching ? <CircularProgress size={14} /> : undefined}
          >
            {failed ? 'Retry' : 'Load more'}
          </Button>
        </Box>
      )}
    </>
  );
};

export const EventCatalogDetailPanel = ({
  eventType,
  onClose,
}: {
  eventType: string;
  onClose?: () => void;
}) => {
  // No includeSample: the Events section below carries live payloads,
  // so the detail fetch stays cheap (no ClickHouse touch).
  const { data, isLoading, error } = useGetEventCatalogEntryQuery({ eventType });
  const entry = data?.entry;
  const schemaProperties = useMemo(
    () =>
      Object.entries(
        (data?.payload_schema?.properties ?? {}) as Record<string, { description?: string }>,
      ),
    [data],
  );

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Loading {eventType}…
        </Typography>
      </Paper>
    );
  }
  if (error || !entry) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">Failed to load {eventType}.</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
          {entry.event_type}
        </Typography>
        {onClose && (
          <IconButton size="small" aria-label="Close" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={{ mt: 1 }}>
        <DeclarationChips entry={entry} />
      </Box>

      <Divider sx={{ my: 2 }} />
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
        Observed
      </Typography>
      <ObservedStats entry={entry} />

      <Divider sx={{ my: 2 }} />
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
        Tags
      </Typography>
      <TagEditor entry={entry} />

      <Divider sx={{ my: 2 }} />
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
        Curation
      </Typography>
      <CurationForm entry={entry} />

      {schemaProperties.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
            Payload fields
          </Typography>
          <Box sx={{ mb: 1.5 }}>
            {schemaProperties.map(([name, prop]) => (
              <Typography
                key={name}
                variant="caption"
                sx={{ display: 'block', fontFamily: 'monospace' }}
              >
                {name}
                {prop.description ? (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}
                    — {prop.description}
                  </Typography>
                ) : null}
              </Typography>
            ))}
          </Box>
        </>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
        Events
      </Typography>
      <EventOccurrences eventType={entry.event_type} />
    </Paper>
  );
};
